import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

/**
 * Компонент TemperatureConverterPage - страница конвертера температуры
 */
const TemperatureConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();
  
  // Получаем данные инструмента
  const tool = getToolById('temperature-converter');
  
  // Устанавливаем заголовок страницы
  useEffect(() => {
    document.title = tool?.metaTitle || 'Конвертер температуры | Gensio';
    
    // Находим мета-описание и устанавливаем его, если есть
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && tool?.metaDescription) {
      metaDescription.setAttribute('content', tool.metaDescription);
    }
  }, [tool]);
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-25 pointer-events-none"></div>
      
      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Хлебные крошки */}
        <Breadcrumbs />
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Основной контент */}
          <div className="lg:w-2/3">
            {/* Заголовок и описание */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {tool?.name || 'Конвертер температуры'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {tool?.description || 'Перевод между Цельсием, Фаренгейтом, Кельвином и другими температурными шкалами.'}
              </p>
            </div>
            
            {/* Заглушка для контента */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
              <div className="p-12 text-center">
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  Конвертер температуры находится в разработке. Скоро здесь появится полная функциональность.
                </p>
              </div>
            </div>
            
            {/* Информация об инструменте */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                О конвертере температуры
              </h2>
              
              <div className="prose dark:prose-invert max-w-none">
                <p>
                  Конвертер температуры позволяет легко переводить значения температуры между различными шкалами: Цельсия, Фаренгейта, Кельвина и другими. Это особенно полезно при путешествиях в страны, где используются другие температурные системы.
                </p>
                
                <p>
                  С помощью нашего конвертера вы сможете:
                </p>
                
                <ul>
                  <li>Переводить температуру из Цельсия в Фаренгейт и обратно</li>
                  <li>Конвертировать значения в шкалу Кельвина</li>
                  <li>Получать точные результаты с подробными расчетами</li>
                  <li>Использовать дополнительные температурные шкалы (Реомюра, Ранкина)</li>
                  <li>Узнать интересные факты о разных температурных системах</li>
                </ul>
                
                <p>
                  <strong>Основные температурные шкалы:</strong>
                </p>
                
                <ul>
                  <li><strong>Цельсий (°C)</strong> - самая распространенная шкала в мире. Точка замерзания воды - 0°C, точка кипения - 100°C.</li>
                  <li><strong>Фаренгейт (°F)</strong> - используется в США и некоторых других странах. Точка замерзания воды - 32°F, точка кипения - 212°F.</li>
                  <li><strong>Кельвин (K)</strong> - абсолютная шкала температур, используемая в науке. 0 K соответствует абсолютному нулю, при котором прекращается движение молекул. 273.15 K = 0°C.</li>
                </ul>
                
                <p>
                  <strong>Формулы перевода:</strong>
                </p>
                
                <ul>
                  <li>Из Цельсия в Фаренгейт: °F = °C × 9/5 + 32</li>
                  <li>Из Фаренгейта в Цельсий: °C = (°F - 32) × 5/9</li>
                  <li>Из Цельсия в Кельвины: K = °C + 273.15</li>
                  <li>Из Кельвинов в Цельсий: °C = K - 273.15</li>
                </ul>
                
                <p>
                  Скоро здесь появится полностью функциональный конвертер температуры с дополнительными возможностями и таблицами для сравнения.
                </p>
              </div>
            </div>
          </div>
          
          {/* Боковая панель */}
          <div className="lg:w-1/3">
            <ToolSidebar 
              currentToolId="temperature-converter"
              currentSubToolId={subToolId}
              tool={tool}
            />
          </div>
        </div>
      </main>
      
      {/* Футер */}
      <Footer />
    </div>
  );
};

export default TemperatureConverterPage;